import {
  Alert,
  Box,
  Button,
  CloseButton,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  NativeSelect,
  Separator,
  Tag,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router'

import type { ToastLike } from '@mangovoice/mango-ui'
import { Field, LoadingState } from '@mangovoice/mango-ui'
import { ExtensionType } from '@mangovoice/types'

import type { MessagingApi, PhoneNumberDetail } from '../api/messagingApi.types'
import { hasValidCampaignId } from '../api/messagingApi.types'
import { AssociatedSmsNumbersModal } from '../components/AssociatedSmsNumbersModal'
import { MemberManagement } from '../components/MemberManagement'
import {
  useAddMessageGroupMember,
  useGetAvailableSmsNumbers,
  useGetExtensions,
  useGetMessageGroup,
  useGetMessageGroupMembers,
  useRemoveMessageGroupMember,
  useUpdateMessageGroup,
} from '../queries/messagingQueries'
import type { MessagingTranslateFn } from '../types/i18n.types'
import { text } from '../types/i18n.types'

const formatPhoneDisplay = (raw: string): string => {
  const digits = raw.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return raw
}

interface PrimaryOption {
  id: number
  label: string
  campaign_id?: string | null
}

/**
 * Normalized phone-number info the edit form needs to render a badge, a
 * primary dropdown option, or the add-SMS modal. `group.phone_numbers`
 * items provide `number_for_display`; `availableSmsNumbers` (all PBX
 * numbers not assigned to any group) do not, so we format them from
 * `number` to keep badge labels consistent across sources.
 */
interface PhoneInfo {
  id: number
  name: string
  number: string
  number_for_display: string
  campaign_id?: string | null
}

const arraysEqualAsSets = (a: number[], b: number[]): boolean => {
  if (a.length !== b.length) return false
  const sortedA = [...a].sort((x, y) => x - y)
  const sortedB = [...b].sort((x, y) => x - y)
  return sortedA.every((v, i) => v === sortedB[i])
}

export interface MessagingDetailPageProps {
  /** Messaging API instance */
  api: MessagingApi
  /** PBX ID */
  pbxId: number
  /** Group ID to edit */
  groupId: number
  /** Toast notifications */
  toast: ToastLike
  /** Optional translation function for i18n; when omitted, components use fallback English */
  t?: MessagingTranslateFn
  /** When false, campaign ID warnings are hidden (e.g. UK does not require campaign registration) */
  campaignIdRequired?: boolean
  /** Path to the messaging list page; when provided, a "Back to list" link is rendered */
  listPath?: string
  /**
   * Convenience permission prop that gates every management action
   * (edit group metadata, manage members). Granular props below take
   * precedence when provided. Defaults to `false` — consumers must
   * explicitly opt in to management controls. This mirrors the legacy
   * admin's server-driven `user_can` gates on MESSAGING.
   */
  canManageMessageGroups?: boolean
  /**
   * Gates editing the group name, primary SMS selection, and
   * adding/removing associated secondary SMS numbers. Falls back to
   * `canManageMessageGroups`.
   */
  canEdit?: boolean
  /**
   * Gates adding and removing members. Falls back to
   * `canManageMessageGroups`.
   */
  canManageMembers?: boolean
}

export const MessagingDetailPage: React.FC<MessagingDetailPageProps> = ({
  api,
  pbxId,
  groupId,
  toast,
  t,
  campaignIdRequired = true,
  listPath,
  canManageMessageGroups = false,
  canEdit,
  canManageMembers,
}) => {
  const effectiveCanEdit = canEdit ?? canManageMessageGroups
  const effectiveCanManageMembers = canManageMembers ?? canManageMessageGroups
  // The edit form holds *staged* state for name, primary SMS, and
  // secondary SMS numbers. Nothing reaches the server until the user
  // clicks "Update Message Group", at which point `handleUpdate` commits
  // all three fields in a single PATCH. This mirrors the "Unsaved
  // changes" indicator and prevents the surprise auto-save that the
  // previous UI had when adding/removing secondary badges.
  const [name, setName] = useState('')
  const [primarySmsId, setPrimarySmsId] = useState<string>('')
  const [secondarySmsIds, setSecondarySmsIds] = useState<number[]>([])
  const [isSmsModalOpen, setIsSmsModalOpen] = useState(false)
  const [savedName, setSavedName] = useState('')
  const [savedPrimarySmsId, setSavedPrimarySmsId] = useState('')
  const [savedSecondarySmsIds, setSavedSecondarySmsIds] = useState<number[]>([])

  const {
    data: group,
    isLoading: isLoadingGroup,
    error: groupError,
  } = useGetMessageGroup(api, groupId)

  const { data: members = [], isLoading: isLoadingMembers } =
    useGetMessageGroupMembers(api, groupId)

  const { data: extensions = [], isLoading: isLoadingExtensions } =
    useGetExtensions(api, pbxId)

  const availableMembers = useMemo(
    () =>
      extensions.filter(
        (ext) =>
          ext.type === ExtensionType.LINE &&
          // Defensive: the API already filters via
          // `?type=ExtensionLine&status_filter=active` (see
          // `messagingApi.getExtensions`), but we re-check here so a
          // backend revision can't silently reintroduce the bug. The
          // serializer exposes `active` as a JSON boolean, so only
          // `false` hides a line — `undefined` falls through (treated
          // as active) since not every response surfaces the field.
          ext.active !== false &&
          !members.some((m) => m.extension_id === ext.id),
      ),
    [extensions, members],
  )

  const { data: availableSmsNumbers = [], isLoading: isLoadingSmsNumbers } =
    useGetAvailableSmsNumbers(api, pbxId)

  const updateMutation = useUpdateMessageGroup(api, groupId, pbxId)
  const addMemberMutation = useAddMessageGroupMember(api, groupId, pbxId)
  const removeMemberMutation = useRemoveMessageGroupMember(api, groupId, pbxId)

  // Only seed the staged state from the server response the first time
  // we see a given group id. Otherwise an unrelated cache invalidation
  // (another tab, a member mutation, etc.) would refetch `group` and
  // silently overwrite the user's in-progress edits. After a successful
  // save we manually reset the `saved*` baseline from what we sent, so
  // the subsequent refetch is a no-op for this effect.
  const initializedGroupIdRef = useRef<number | null>(null)
  useEffect(() => {
    if (group && initializedGroupIdRef.current !== group.id) {
      initializedGroupIdRef.current = group.id
      setName(group.name)
      setSavedName(group.name)
      const primary = group.phone_numbers.find((p) => p.is_primary_sms)
      const primaryId = primary ? String(primary.id) : ''
      setPrimarySmsId(primaryId)
      setSavedPrimarySmsId(primaryId)
      const secondaryIds = group.phone_numbers
        .filter((p) => !p.is_primary_sms)
        .map((p) => p.id)
      setSecondarySmsIds(secondaryIds)
      setSavedSecondarySmsIds(secondaryIds)
    }
  }, [group])

  const isDirty =
    name !== savedName ||
    primarySmsId !== savedPrimarySmsId ||
    !arraysEqualAsSets(secondarySmsIds, savedSecondarySmsIds)

  const handleUpdate = useCallback(() => {
    if (!name.trim()) {
      toast.create({
        title: text(
          t,
          'messaging.detail.validationErrorTitle',
          'Validation Error',
        ),
        description: text(
          t,
          'messaging.detail.validationErrorDescription',
          'Group name is required',
        ),
        type: 'error',
        duration: 3000,
        closable: true,
      })
      return
    }

    const newPrimaryId = primarySmsId ? parseInt(primarySmsId, 10) : undefined
    // The Primary SMS dropdown includes existing secondaries as valid
    // options so users can promote one, but the backend rejects a payload
    // where the same number appears as both primary and secondary. Drop
    // the primary from secondaries in the outgoing payload (and the local
    // baseline) so promoting implicitly demotes in a single save.
    const dedupedSecondaryIds = secondarySmsIds.filter(
      (id) => id !== newPrimaryId,
    )

    updateMutation.mutate(
      {
        name: name.trim(),
        primary_phone_number_id: newPrimaryId,
        // Always send the full list (including `[]`) so the server can
        // distinguish "clear all secondaries" from "leave untouched".
        secondary_phone_number_ids: dedupedSecondaryIds,
      },
      {
        onSuccess: () => {
          const trimmedName = name.trim()
          setName(trimmedName)
          setSavedName(trimmedName)
          setSavedPrimarySmsId(primarySmsId)
          setSecondarySmsIds(dedupedSecondaryIds)
          setSavedSecondarySmsIds(dedupedSecondaryIds)
          toast.create({
            title: text(
              t,
              'messaging.detail.toast.updatedTitle',
              'Message group updated',
            ),
            type: 'success',
            duration: 3000,
            closable: true,
          })
        },
        onError: (error) => {
          toast.create({
            title: text(
              t,
              'messaging.detail.toast.updateFailedTitle',
              'Update failed',
            ),
            // Surface the real API message (e.g. DRF validation errors
            // like "A phone number cannot be both primary and secondary")
            // instead of a generic fallback. `messagingApi.ts` already
            // extracts detail text from standardized-error payloads, so
            // `error.message` is the user-facing validation string.
            description:
              error.message ||
              text(
                t,
                'messaging.detail.toast.updateFailedDescription',
                'Failed to update message group',
              ),
            type: 'error',
            duration: 5000,
            closable: true,
          })
        },
      },
    )
  }, [name, primarySmsId, secondarySmsIds, updateMutation, toast, t])

  const handleAddSmsNumber = useCallback((phoneNumberId: number) => {
    // Staged-only: the new secondary is committed to the server by the
    // next `handleUpdate` save. The Add-SMS modal filters out numbers
    // already staged, so we should never see a duplicate here.
    setSecondarySmsIds((prev) =>
      prev.includes(phoneNumberId) ? prev : [...prev, phoneNumberId],
    )
    setIsSmsModalOpen(false)
  }, [])

  const handleRemoveSmsNumber = useCallback((phoneNumberId: number) => {
    // Staged-only remove. Only secondary badges expose a remove
    // affordance — primary is cleared through the Primary dropdown — so
    // we only need to drop from the secondaries list here.
    setSecondarySmsIds((prev) => prev.filter((id) => id !== phoneNumberId))
  }, [])

  const handleAddMember = useCallback(
    (extensionId: number) => {
      addMemberMutation.mutate(extensionId, {
        onSuccess: () => {
          toast.create({
            title: text(
              t,
              'messaging.detail.toast.memberAddedTitle',
              'Member added',
            ),
            type: 'success',
            duration: 2000,
            closable: true,
          })
        },
        onError: (error) => {
          toast.create({
            title: text(
              t,
              'messaging.detail.toast.memberAddFailedTitle',
              'Failed to add member',
            ),
            // Surface real API error (matches the update-failed handler above).
            description:
              error.message ||
              text(
                t,
                'messaging.detail.toast.memberAddFailedDescription',
                'Failed to add member to the group',
              ),
            type: 'error',
            duration: 5000,
            closable: true,
          })
        },
      })
    },
    [addMemberMutation, toast, t],
  )

  const handleRemoveMember = useCallback(
    (memberId: number) => {
      removeMemberMutation.mutate(memberId, {
        onSuccess: () => {
          toast.create({
            title: text(
              t,
              'messaging.detail.toast.memberRemovedTitle',
              'Member removed',
            ),
            type: 'success',
            duration: 2000,
            closable: true,
          })
        },
        onError: () => {
          toast.create({
            title: text(
              t,
              'messaging.detail.toast.memberRemoveFailedTitle',
              'Failed to remove member',
            ),
            type: 'error',
            duration: 3000,
            closable: true,
          })
        },
      })
    },
    [removeMemberMutation, toast, t],
  )

  // Single source of truth for phone display info. Entries come from
  // either `group.phone_numbers` (has `number_for_display`) or
  // `availableSmsNumbers` (only raw `number`). `group` wins on
  // conflict because its campaign_id reflects the current association
  // state. Memoized (and hoisted above the early returns below) so
  // typing in the name field doesn't rebuild all of this on every
  // keystroke.
  const phoneInfoById = useMemo(() => {
    const map = new Map<number, PhoneInfo>()
    for (const phone of availableSmsNumbers) {
      map.set(phone.id, {
        id: phone.id,
        name: phone.name,
        number: phone.number,
        number_for_display: formatPhoneDisplay(phone.number),
        campaign_id: phone.campaign_id,
      })
    }
    if (group) {
      for (const phone of group.phone_numbers) {
        map.set(phone.id, {
          id: phone.id,
          name: phone.name,
          number: phone.number,
          number_for_display: phone.number_for_display,
          campaign_id: phone.campaign_id,
        })
      }
    }
    return map
  }, [availableSmsNumbers, group])

  // Set of numbers the user currently has staged on the group. The
  // add-SMS modal must exclude these so the user can't pick the same
  // number twice (either as primary or as an existing secondary badge).
  const stagedPrimaryIdNumber = primarySmsId ? parseInt(primarySmsId, 10) : null
  const stagedAssociatedIds = useMemo(() => {
    const set = new Set<number>(secondarySmsIds)
    if (stagedPrimaryIdNumber !== null) set.add(stagedPrimaryIdNumber)
    return set
  }, [secondarySmsIds, stagedPrimaryIdNumber])

  // Numbers offered by the add-SMS modal:
  //  1. PBX numbers not assigned to any group (the `availableSmsNumbers`
  //     query), minus anything already staged.
  //  2. Numbers currently associated to *this* group server-side but
  //     staged for removal. Including them lets the user undo a staged
  //     remove within the same edit session without saving and
  //     re-refetching.
  const numbersForModal: PhoneNumberDetail[] = useMemo(() => {
    const seen = new Set<number>()
    const out: PhoneNumberDetail[] = []
    for (const n of availableSmsNumbers) {
      if (stagedAssociatedIds.has(n.id)) continue
      seen.add(n.id)
      out.push(n)
    }
    if (group) {
      for (const phone of group.phone_numbers) {
        if (stagedAssociatedIds.has(phone.id)) continue
        if (seen.has(phone.id)) continue
        // Normalize a `PhoneNumber` into the `PhoneNumberDetail` shape
        // the modal renders from. Fields the modal doesn't read are
        // stubbed; in particular `sms_activated_at: null` is
        // *intentional* — these entries represent numbers currently
        // associated to this group (so already SMS-provisioned) and the
        // modal never filters on it. If the modal ever starts gating on
        // `sms_activated_at`, this stub would need a real timestamp to
        // avoid silently hiding staged-removal undo candidates.
        out.push({
          id: phone.id,
          pbx_id: pbxId,
          name: phone.name,
          number: phone.number,
          sms_activated_at: null,
          campaign_id: phone.campaign_id,
        })
      }
    }
    return out
  }, [availableSmsNumbers, group, stagedAssociatedIds, pbxId])

  // Primary dropdown options: every PBX number we know about, so the
  // user can promote an existing secondary or pick any unassigned
  // number. Deduped via the map above.
  const primaryOptions: PrimaryOption[] = useMemo(
    () =>
      Array.from(phoneInfoById.values()).map((phone) => ({
        id: phone.id,
        label: `${phone.number_for_display} (${phone.name})`,
        campaign_id: phone.campaign_id,
      })),
    [phoneInfoById],
  )

  if (isLoadingGroup) {
    return (
      <LoadingState
        layout="alert"
        title={text(t, 'messaging.detail.loading', 'Loading message group...')}
      />
    )
  }

  if (groupError) {
    return (
      <Alert.Root status="error" p={4}>
        <Alert.Indicator />
        <Box>
          <Text fontWeight="bold">
            {text(t, 'messaging.detail.error', 'Error')}
          </Text>
          <Text>{(groupError as Error).message}</Text>
        </Box>
      </Alert.Root>
    )
  }

  if (!group) {
    return (
      <Alert.Root status="warning" p={4}>
        <Alert.Indicator />
        <Text>
          {text(t, 'messaging.detail.notFound', 'Message group not found.')}
        </Text>
      </Alert.Root>
    )
  }

  return (
    <Box flex="1" w="full" p={6} overflow="auto">
      <VStack gap={6} align="stretch">
        {/* Edit Form */}
        <Box>
          <HStack
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={4}
            mb={5}
          >
            <VStack
              gap={3}
              align="flex-start"
              flexDirection={'row'}
              alignItems={'center'}
            >
              {listPath && (
                <Link to={listPath} style={{ textDecoration: 'none' }}>
                  <Text fontSize="sm" color="fg.muted" _hover={{ color: 'fg' }}>
                    {text(t, 'messaging.detail.backToList', '← Back to list')}
                  </Text>
                </Link>
              )}
              <Heading fontSize="2xl" mb={0}>
                {effectiveCanEdit
                  ? text(t, 'messaging.detail.title', 'Edit Message Group')
                  : text(t, 'messaging.detail.viewTitle', 'Message Group')}
              </Heading>
            </VStack>
            {effectiveCanEdit && (
              <HStack gap={3}>
                {isDirty && (
                  <Text fontSize="sm" color="fg.warning" fontWeight="medium">
                    {text(
                      t,
                      'messaging.detail.unsavedChanges',
                      'Unsaved changes',
                    )}
                  </Text>
                )}
                <Button
                  colorPalette="brand"
                  borderRadius="lg"
                  onClick={handleUpdate}
                  disabled={updateMutation.isPending || !isDirty}
                >
                  {updateMutation.isPending
                    ? text(t, 'messaging.detail.updating', 'Updating...')
                    : text(
                        t,
                        'messaging.detail.updateButton',
                        'Update Message Group',
                      )}
                </Button>
              </HStack>
            )}
          </HStack>
          <VStack gap={5} align="stretch" maxW="600px">
            {campaignIdRequired &&
              (() => {
                // Base the warning on the *staged* association (primary +
                // secondaries) rather than the server's current state, so
                // the warning reflects what will actually be saved.
                const stagedIds: number[] = []
                if (stagedPrimaryIdNumber !== null) {
                  stagedIds.push(stagedPrimaryIdNumber)
                }
                stagedIds.push(...secondarySmsIds)
                const misconfiguredNumbers = stagedIds
                  .map((id) => phoneInfoById.get(id))
                  .filter((p): p is PhoneInfo => !!p)
                  .filter((p) => !hasValidCampaignId(p))
                if (misconfiguredNumbers.length === 0) return null
                return (
                  <Alert.Root status="warning" size="sm" borderRadius="md">
                    <Alert.Indicator />
                    <Box>
                      <Text fontWeight="medium" fontSize="sm">
                        {text(
                          t,
                          'messaging.detail.campaignSummaryTitle',
                          `${misconfiguredNumbers.length} number(s) missing campaign ID`,
                          { count: misconfiguredNumbers.length },
                        )}
                      </Text>
                      <Text fontSize="xs" color="fg.muted">
                        {misconfiguredNumbers
                          .map((p) => p.number_for_display)
                          .join(', ')}
                        {' — '}
                        {text(
                          t,
                          'messaging.detail.campaignSummaryDescription',
                          'These numbers may not be able to send SMS unless they are toll-free or Canadian numbers.',
                        )}
                      </Text>
                    </Box>
                  </Alert.Root>
                )
              })()}

            <Field.Root required>
              <Field.Label>
                {text(t, 'messaging.detail.groupNameLabel', 'Group Name')}
              </Field.Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={text(
                  t,
                  'messaging.detail.groupNamePlaceholder',
                  'Enter group name',
                )}
                px={3}
                // readOnly (not disabled) keeps the field focusable and its
                // value selectable/copyable for view-only users. The Save
                // button is hidden separately so no edits can be submitted.
                readOnly={!effectiveCanEdit}
              />
            </Field.Root>

            <Field.Root>
              <Field.Label>
                {text(
                  t,
                  'messaging.detail.primarySmsLabel',
                  'Primary SMS number',
                )}
              </Field.Label>
              <NativeSelect.Root disabled={!effectiveCanEdit}>
                <NativeSelect.Field
                  value={primarySmsId}
                  onChange={(e) => setPrimarySmsId(e.target.value)}
                >
                  <option value="">
                    {text(
                      t,
                      'messaging.detail.primarySmsPlaceholder',
                      'Select primary SMS number',
                    )}
                  </option>
                  {primaryOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </NativeSelect.Field>
              </NativeSelect.Root>
            </Field.Root>

            <Field.Root>
              <Field.Label>
                {text(
                  t,
                  'messaging.detail.associatedSmsLabel',
                  'Secondary SMS numbers',
                )}
              </Field.Label>
              <HStack gap={2} flexWrap="wrap">
                {secondarySmsIds
                  .map((id) => phoneInfoById.get(id))
                  .filter((phone): phone is PhoneInfo => !!phone)
                  .map((phone) => {
                    const misconfigured =
                      campaignIdRequired && !hasValidCampaignId(phone)
                    const tag = (
                      <Tag.Root
                        key={phone.id}
                        colorPalette={misconfigured ? 'warning' : 'info'}
                        size="lg"
                        borderRadius="full"
                      >
                        <Tag.Label>
                          {phone.number_for_display}
                          {misconfigured && (
                            <Text as="span" fontSize="xs" ml={1}>
                              {text(
                                t,
                                'messaging.detail.noCampaignId',
                                '(No campaign ID)',
                              )}
                            </Text>
                          )}
                        </Tag.Label>
                        {effectiveCanEdit && (
                          <CloseButton
                            size="2xs"
                            ms={1}
                            onClick={() => handleRemoveSmsNumber(phone.id)}
                            disabled={updateMutation.isPending}
                            aria-label={text(
                              t,
                              'messaging.detail.removeSmsAria',
                              'Remove SMS number',
                            )}
                          />
                        )}
                      </Tag.Root>
                    )
                    if (!phone.name) return tag
                    return (
                      <Tooltip.Root
                        key={phone.id}
                        openDelay={200}
                        closeDelay={0}
                        positioning={{ placement: 'top-start' }}
                      >
                        <Tooltip.Trigger asChild>{tag}</Tooltip.Trigger>
                        <Tooltip.Positioner>
                          <Tooltip.Content>{phone.name}</Tooltip.Content>
                        </Tooltip.Positioner>
                      </Tooltip.Root>
                    )
                  })}
                {effectiveCanEdit && (
                  <IconButton
                    aria-label={text(
                      t,
                      'messaging.detail.addSmsNumberAria',
                      'Add SMS number',
                    )}
                    size="xs"
                    colorPalette="brand"
                    variant="subtle"
                    borderRadius="full"
                    onClick={() => setIsSmsModalOpen(true)}
                  >
                    <Icon boxSize={3}>
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                      >
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </Icon>
                  </IconButton>
                )}
              </HStack>
            </Field.Root>
          </VStack>
        </Box>

        <Separator />

        {!isLoadingMembers && members.length === 0 && (
          <Alert.Root status="warning" borderRadius="md">
            <Alert.Indicator />
            <Text fontSize="sm">
              {text(
                t,
                'messaging.detail.noMembersWarning',
                'This group has no members. Customer messages may go unread.',
              )}
            </Text>
          </Alert.Root>
        )}

        {/* Member Management */}
        <MemberManagement
          members={members}
          availableMembers={availableMembers}
          isLoadingMembers={isLoadingMembers}
          isLoadingAvailable={isLoadingExtensions}
          onAddMember={handleAddMember}
          onRemoveMember={handleRemoveMember}
          isAdding={addMemberMutation.isPending}
          isRemoving={removeMemberMutation.isPending}
          canManage={effectiveCanManageMembers}
          t={t}
        />
      </VStack>

      {effectiveCanEdit && (
        <AssociatedSmsNumbersModal
          isOpen={isSmsModalOpen}
          onClose={() => setIsSmsModalOpen(false)}
          availableNumbers={numbersForModal}
          isLoading={isLoadingSmsNumbers}
          onSubmit={handleAddSmsNumber}
          isSubmitting={updateMutation.isPending}
          toast={toast}
          t={t}
        />
      )}
    </Box>
  )
}
